import React, {FormEvent, useEffect, useState} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { makeStyles } from '@material-ui/core/styles';
import DemoIcon from '../icons/DemoIcon';
import Toast, { ToastType } from '../atoms/Toast';
import Loader from '../atoms/Loader';
import { logIn, resetPassword, healthCheck } from '../services/user';
import { getQueryParameter } from '../services/query-parameters';
import { retrieveLastPath } from '../services/path';
import { updatePageTitle } from '../services/page-title';
import { getScreenSize, ScreenSize } from '../services/screen-size';
import { setScreenSizeAction } from '../redux/actions/ui';
import { resetReduxStoreAction } from '../redux/actions/root';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import { COLOR_BLACK, COLOR_BLUE, COLOR_DARK_BLUE, COLOR_DARK_ORANGE, COLOR_SLATE_NAVY, COLOR_TEXT, COLOR_WHITE } from '../styles/colors';
import MEDIA from '../styles/media';
import Outage from '../organisms/Outage';
import { Box, Switch, Typography } from '@material-ui/core';
import clsx from 'clsx';
import ForgetPasswordDialog from './ForgetPasswordDialog';
import SubmitButton from '../atoms/SubmitButton';
import { decodeBase64 } from '../services/converter';

interface LoginFormProps {
  resetReduxStoreAction: () => void;
  setScreenSizeAction: (screenSize:ScreenSize) => void,
  handleChange?: (e: any) => void;
  checked?: boolean;
  applicationType?: string | null;
  message?: string | null;
}

const useStyles = makeStyles(theme => ({
  loginForm: {
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'proxima-nova, Arial, sans-serif',
    width: '100%',
    maxWidth: '430px',
  },
  title: {
    // ...theme.typography.h1,
    fontWeight: 800,
    fontSize: theme.typography.pxToRem(40),
    lineHeight: 1.5,
    margin: theme.spacing(0, 0, 3.75),
  },
  input: {
    marginTop: theme.spacing(2),

    '& label': {
      display: 'block',
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 500,
      lineHeight: 1.2,
      marginBottom: theme.spacing(1.25),
    },

    '& input': {
      backgroundColor: '#ebf1f6',
      border: '1px solid #1a588d',
      borderRadius: 40,
      fontSize: theme.typography.pxToRem(16),
      fontWeight: 400,
      lineHeight: 1.2,
      minHeight: 42,
      padding: theme.spacing(0.5, 2),
      width: '100%',
    }
  },

  submitWrapper: {
    marginTop: theme.spacing(3),
    display: 'flex',
  },

  forgotPassword: {
    marginLeft: theme.spacing(2),
    // padding: theme.spacing(2),
    appearance: 'none',
    background: 'none',
    border: 0,
    margin: 0,
    fontFamily: FONT_PROXIMA_NOVA,
    fontWeight: 700,
    fontSize: theme.typography.pxToRem(16),
    lineHeight: 1,
    color: '#00578d',
    textDecoration: 'underline',
    cursor: 'pointer',
    outline: 'none',
    transition: 'opacity 0.2s ease, transform 0.2s ease',
    opacity: 1,
    transform: 'scale(1)',

    '&:hover': {
      color: "#00578d",
      textDecoration: 'underline',
      textDecorationThickness: '0.2em',
    },
    '&:focus': {
      fontWeight: 'bold',
    },
  },

  footer: {
    position: 'absolute',
    bottom: '150px',
    display: 'flex',
    flexDirection: 'column',
  },
  signupWrapper: {
    display: 'flex',
  },
  signupLink: {
    marginLeft: theme.spacing(1),
    color: COLOR_BLUE,
    textDecoration: 'underline',

    '&:hover': {
      color: COLOR_DARK_BLUE,
    },
    '&:focus': {
      fontWeight: 'bold',
    },
  },

  demoLink: {
    marginTop: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    '&:hover $demoIcon': {
      color: COLOR_DARK_BLUE,
    },
    '&:hover $demoText': {
      color: COLOR_DARK_BLUE,
    },
    '&:focus $demoText': {
      fontWeight: 'bold',
    },
  },
  demoIcon: {
    width: '32px',
    height: '32px',
    color: COLOR_BLUE,
  },
  demoText: {
    marginLeft: theme.spacing(1),
    color: COLOR_BLUE,
    textDecoration: 'underline',
  },

  resetPasswordText: {
    margin: theme.spacing(3.75, 0),
    fontFamily: FONT_PROXIMA_NOVA,
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 600,
    lineHeight: 1.5,
    color: COLOR_TEXT,
  },
  resetInputField: {
    padding: theme.spacing(2.5, 2, 1),
  },

  '@media (max-height: 800px)': {
    footer: {
      position: 'relative',
      bottom: 'auto',
      marginTop: theme.spacing(8),
    },
  },

  [MEDIA.MOBILE]: {
    title: {
      fontSize: theme.typography.pxToRem(20),
      color: COLOR_TEXT,
    },

    footer: {
      position: 'relative',
      bottom: 'auto',
      marginTop: theme.spacing(8),
    },
  },

  '@media (max-width: 400px)': {
    forgotPassword: {
      padding: 0,
    },
  },
  dualLabelSwitch: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',

    '& .MuiTypography-root:first-of-type': {
      minWidth: '130px',
      paddingRight: '4px',
    },

    '& .MuiTypography-root:last-of-type': {
      minWidth: '160px',
      paddingLeft: '8px',
    },
  },
  switchText: {
    fontWeight: 400,
    lineHeight: 1.2,
    transition: 'font-weight 0.2s ease-in-out',
  },
  switchRoot: {
    display: 'flex',
    height: 52,
    padding: 0,
    width: 96,
  },
  switchBase: {
    padding: 6,
    color: COLOR_WHITE,
    '&$checked': {
      transform: 'translateX(45px)',
      color: COLOR_WHITE,
      '& + $track': {
        backgroundColor: COLOR_DARK_BLUE,
        border: `1px solid ${COLOR_DARK_BLUE}`,
        opacity: 1,
      },
    },
  },
  thumb: {
    backgroundColor: COLOR_WHITE,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '30px 18px',
    boxShadow: '0 3px 6px rgba(0,0,0,0.3)',
    borderRadius: '50%',
    height: 40,
    width: 40,
  },
  thumbImageUnchecked: {
    backgroundImage: `url('/svgs/tf-logo-mark.svg')`,
  },
  thumbImageChecked: {
    backgroundImage: `url('/svgs/ssa-logo-mark.svg')`,
  },
  track: {
    backgroundColor: COLOR_DARK_ORANGE,
    border: '1px solid',
    borderRadius: 48,
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 300,
    }),
  },
  checked: {},
  requiredLabel: {
    '&::after': {
      content: '" *"',
      color: 'red',
    },
  },
  forgetPasswordHidden: {
    opacity: 0,
    transform: 'scale(0.95)',
    pointerEvents: 'none',
    height: 0,
    overflow: 'hidden',
  },
  activeLabel: {
    fontWeight: 700,
    lineHeight: 1,
  },
  resetPasswordInputWrap: {
    margin: theme.spacing(2.5, 0),

    '& label': {
      lienHeight: 1.2,
      fontWeight: 600,

      '&::after': {
        content: '" *"',
        color: 'red',
      },
    },

    '& input': {
      backgroundColor: '#ebf1f6',
      border: '1px solid #00578d',
      borderRadius: '30px',
      fontSize: theme.typography.pxToRem(16),
      lienHeight: 1.2,
      marginTop: theme.spacing(1),
      minHeight: 42,
      padding: theme.spacing(0.5, 2),
      width: '100%',
    }
  },
}), { name: LoginForm.name });

function LoginForm (props:LoginFormProps) {
  const {
    resetReduxStoreAction,
    setScreenSizeAction,
    handleChange,
    checked,
    applicationType,
    message
  } = props;

  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [email, setEmail] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');

  const [loading, setLoading] = useState<boolean>(false);
  const [forgotPasswordDialogOpen, setForgotPasswordDialogOpen] = useState<boolean>(false);
  const [toastVisible, setToastVisible] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<ToastType>(ToastType.SUCCESS);

  const [isAbleToLogin, setIsAbleToLogin] = useState<boolean>(false);
  const [outageMessage, setOutageMessage] = useState<string>('');

  useEffect(() => {
    if (message) {
      const toastMessage = getMessage(); 
      setToastVisible(true);
      showToast(toastMessage, ToastType.ERROR);
    }
    updatePageTitle('Login');
    onLoad();
  }, []);

  function getMessage () {
    const message = params.get('message');
    let toastMessage = "Login attempt failed. Please check your email and password, and try again.";
    if (message) {
      return decodeBase64(message) || toastMessage;
    }
    return toastMessage;
  }

  function onLoad () {
    setLoading(true);
    healthCheck()
    .then((res) => {
      if (res && res.loginAllowed) {
        setIsAbleToLogin(true);
      } else if (res && !res.loginAllowed) {
        setOutageMessage('Sorry the website is down temporarily for planned updating and maintenance.  We will be back online shortly. Thank you for your patience.');
      }
      setLoading(false);
    })
    .catch((error) => {
      console.error(error);
      setOutageMessage('Sorry the website is down. We are working hard to resolve the issue and will have the site back online as soon as possible. Thank you for your patience.');
      setLoading(false);
    })
  }

  function onLogIn (event:FormEvent) {
    event.preventDefault();

    if (checkFieldsAreValid(email, password)) {
      setLoading(true);

      logIn(email, password)
        .then(() => {
          resetReduxStoreAction();
          setScreenSizeAction(getScreenSize());

          const redirectPath = getQueryParameter('redirect');
          const lastPath = retrieveLastPath();

          history.push(redirectPath ? redirectPath : (lastPath ? lastPath : `/`));
        })
        .catch(error => {
          console.error(error);
          showToast(error.message, ToastType.ERROR);
        })
        .finally(() => setLoading(false));
    }
  }

  function onResetPassword () {
    setLoading(true);

    resetPassword(email)
      .then(() => {
        setForgotPasswordDialogOpen(false);
        showToast('Success. Check you email.', ToastType.SUCCESS);
      })
      .catch((error) => {
        console.error(error);
        showToast('Oops, something is wrong. Try again or contact our support team.', ToastType.ERROR);
      })
      .finally(() => setLoading(false));
  }

  function checkFieldsAreValid (email:string, password:string) {
    let valid = true;

    if (email.length < 6 || !email.includes('@')) {
      setEmailError('Email is invalid');
      valid = false;
    } else {
      setEmailError('');
    }

    if (!password.length) {
      valid = false;
      setPasswordError('Please enter password');
    } else {
      setPasswordError('');
    }

    return valid;
  }

  function showToast (message:string, toastType:ToastType = ToastType.SUCCESS) {
    setToastMessage(message)
    setToastVisible(true);
    setToastType(toastType);
  }

  return (
    !isAbleToLogin ? (
      <Outage outageMessage={outageMessage}/>
    )
    :
    <>
      <div className={classes.loginForm}>
        <Loader inProgress={loading} />

        <h1 className={classes.title}>Login</h1>

          <Box className={classes.dualLabelSwitch}>
            <Typography variant="body1" className={clsx(classes.switchText, !checked && classes.activeLabel)}>Tracking Football</Typography>
            <Switch 
              checked={checked} 
              onChange={handleChange} 
              color="primary" 
              classes={{
                root: classes.switchRoot,
                switchBase: classes.switchBase,
                thumb: clsx(
                  classes.thumb,
                  checked ? classes.thumbImageChecked : classes.thumbImageUnchecked
                ),
                track: classes.track,
                checked: classes.checked,
              }}
            />
            <Typography variant="body1" className={clsx(classes.switchText, checked && classes.activeLabel)}>SportSource Analytics</Typography>
          </Box>


          {
            checked && (
              <form
                className={classes.loginForm}
                action={`${process.env.REACT_APP_SSA_HOST}`}
                method="POST"
              >
                <div className={classes.input}>
                  <label htmlFor="username" className={classes.requiredLabel}>
                    Username
                  </label>
                  <input 
                    type="text" 
                    id="username" 
                    name="username" 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                  {emailError && <p>{emailError}</p>}
                </div>
                <div className={classes.input}>
                  <label htmlFor="password" className={classes.requiredLabel}>
                    Password
                  </label>
                  <input 
                    type="password" 
                    id="password" 
                    name="password" 
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required 
                  />
                  {passwordError && <p>{passwordError}</p>}
                </div>
                <div className={classes.submitWrapper}>
                  <SubmitButton
                    text="Login"
                    type="submit"
                  />
                </div>
              </form>
            )
          }

          {
            !checked && (
            <form
              className={classes.loginForm}
              onSubmit={onLogIn}
            >
              <div className={classes.input}>
                <label htmlFor="email" className={classes.requiredLabel}>
                  Email
                </label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
                {emailError && <p>{emailError}</p>}
              </div>
              <div className={classes.input}>
                <label htmlFor="password" className={classes.requiredLabel}>
                  Password
                </label>
                <input 
                  type="password" 
                  id="password" 
                  name="password" 
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required 
                />
                {passwordError && <p>{passwordError}</p>}
              </div>
              <div className={classes.submitWrapper}>
                <SubmitButton
                  text="Login"
                  type="submit"
                  onSubmit={() => {}}
                />
                  <button
                    className={clsx(classes.forgotPassword, checked && classes.forgetPasswordHidden)}
                    onClick={(event) => {
                      event.preventDefault();
                      setForgotPasswordDialogOpen(true);
                    }}
                  >
                    Forgot password?
                  </button>
              </div>
            </form> 
            )
          }
      </div>

      <div className={classes.footer}>
        <a
          className={classes.demoLink}
          href='https://www.trackingfootball.com/demo/'
        >
          <DemoIcon className={classes.demoIcon} />
          <span className={classes.demoText}>Request a Free Demo</span>
        </a>
      </div>
      <ForgetPasswordDialog
        open={forgotPasswordDialogOpen}
        resetPasswordActions={{onclick: onResetPassword, disabled: !!emailError}}
        onClose={() => setForgotPasswordDialogOpen(false)}
      >
        <Loader inProgress={loading} />
        <p className={classes.resetPasswordText}>
          Enter your email address, and we'll send you an email with a special link to reset your password.
        </p>
        <div className={classes.resetPasswordInputWrap}>
          <label htmlFor="email">
            Email
          </label>
          <input 
            type="email" 
            id="email" 
            name="email" 
            value={email}
            required
            onChange={e => setEmail(e.target.value)}
          />
          {emailError && <p>{emailError}</p>}
        </div>
      </ForgetPasswordDialog>

      <Toast
        visible={toastVisible}
        type={toastType}
        autoHideDuration={null}
        onHide={() => setToastVisible(false)}
      >
        {toastMessage}
      </Toast>
    </>
  )
}

const mapDispatchToProps = (dispatch:Dispatch) => {
  return bindActionCreators(
    {
      resetReduxStoreAction,
      setScreenSizeAction,
    },
    dispatch
  )
};

export default connect(
  null,
  mapDispatchToProps,
)(LoginForm);
